import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import * as Sentry from '@sentry/react';
import { Box, Button, Card, Typography, useTheme } from '@mui/material';
import { SnackBarMessage } from '@eposnow/ui-core';
import { StepContext } from '../../../context/StepContext';
import PageHeader from '../../../components/pageHeaderComponents/PageHeader';
import ActionsContainer from '../../../components/ActionsContainer';
import ProductTableCompleteWrapper from './ProductTableCompleteWrapper';
import SidekickDesktopProductTable from './SidekickDesktopProductTable';
import { ApiContext, ErrorObject } from '../../../context/ApiContext';
import { Category, CategoryRaw, ProductCreateEdit, ProductSuggestion } from '../../../types';
import { CATEGORIES_API_CACHE_KEY } from '../../../constants';
import { API_V1_HOSTNAME_KEY } from '../../../localStorageKeys';
import useFiltersFetchedData from '../../../helperHooks/useFiltersFetchedData';
import { UserContext } from '../../../context/UserContext';
import { UIContext } from '../../../context/UIContext';
import Loader from '../../../components/Loader';
import useBusinessInformation from '../../../helperHooks/useBusinessInformation';
import industryTypes from '../../../helpers/hardcodedIndustryTypes';
import useCountries from '../../../helpers/useCountries';
import SidekickMobileProductsTable from './SidekickMobileProductsTable';
import SkipStepPopup from '../../../components/SkipStepPopup';
import ErrorLoadingData from '../../../components/ErrorLoadingData';

const SidekickProducts = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { apiFetch } = useContext(ApiContext);
  const { locale } = useContext(UserContext);
  const { isMobile } = useContext(UIContext);
  const { goToNextStep } = useContext(StepContext);
  const { countries } = useCountries();
  const [successMessage, setSuccessMessage] = useState('');
  const [businessSuggestionsId, setBusinessSuggestionsId] = useState('');
  const [isStartingSidekick, setIsStartingSidekick] = useState(true);
  const [sidekickRetries, setSidekickRetries] = useState(10);
  const [isNoProductPopupOpen, setIsNoProductPopupOpen] = useState(false);
  const [hasAddedProducts, setHasAddedProducts] = useState(false);
  const {
    businessInformation,
    isLoading: isLoadingBusinessInfo,
    isError,
    refetchBusinessInformation,
  } = useBusinessInformation();

  // Method to get the id to fetch the product suggestions
  const { isLoading: isLoadingBusinessSuggestionId, mutate: getBusinessSuggestionsId } =
    useMutation(
      (body: { industryType: string; location: string }) =>
        apiFetch(
          `${localStorage.getItem(API_V1_HOSTNAME_KEY)}onboarding/proxy/ai-product-suggestions`,
          body,
          'POST'
        ),
      {
        onSuccess(response: { id: string }) {
          setBusinessSuggestionsId(response.id);
        },
        onError(error: ErrorObject) {
          setIsStartingSidekick(false);

          if (error?.code === 401) return;
          Sentry.captureException(
            new Error('Error getting Business Suggestion Id (POST request)'),
            {
              extra: { error: JSON.stringify(error) },
            }
          );
        },
      }
    );

  // Method to fetch the Product suggestions
  // Disabled up until the id needed is available
  const { data, isLoading, isRefetching, refetch } = useQuery<{
    products: ProductSuggestion[];
    status: string;
  }>(
    ['sidekickProductSuggestions'],
    () =>
      apiFetch(
        `${localStorage.getItem(
          API_V1_HOSTNAME_KEY
        )}onboarding/proxy/ai-product-suggestions/${businessSuggestionsId}`
      ),
    {
      enabled: businessSuggestionsId !== '',
      onSuccess(response: { products: ProductSuggestion[]; status: string }) {
        if (response?.status !== 'SUCCESS' && response?.status !== 'ERROR') {
          if (sidekickRetries > 0) {
            setSidekickRetries(sidekickRetries - 1);
            setTimeout(refetch, 15000);
          } else {
            setIsStartingSidekick(false);
          }
        } else {
          setIsStartingSidekick(false);
        }
      },
      onError(error: ErrorObject) {
        setIsStartingSidekick(false);
        if (error?.code === 401) return;
        Sentry.captureException(new Error('Error loading suggestions.'), {
          extra: { error: JSON.stringify(error) },
        });
      },
    }
  );

  // Get and format the categories list
  const { data: categoryRawData, isLoading: isLoadingCategories } = useFiltersFetchedData<{
    Data: CategoryRaw[];
    Metadata: {
      Page: number;
      Limit: number;
      TotalPages: number;
      TotalRecords: number;
      CurrentPageSize: number;
    };
  }>(
    CATEGORIES_API_CACHE_KEY,
    `${localStorage.getItem(API_V1_HOSTNAME_KEY)}onboarding/proxy/categories`,
    'categories'
  );

  const categoriesData: Category[] = useMemo(
    () =>
      categoryRawData && categoryRawData?.Data?.length > 0
        ? categoryRawData?.Data?.map((x) => ({
            ...x,
            id: x?.Id,
          }))
        : [],
    [categoryRawData]
  );

  const tableData: ProductSuggestion[] = useMemo(
    () =>
      data?.products && data?.products?.length > 0
        ? data?.products?.map((product) => ({
            ...product,
            categoryId:
              categoriesData?.find((category) => category?.Id?.toString() === product?.category?.id)
                ?.Id || -1,
          }))
        : [],
    [data, categoriesData]
  );

  // Add product(s) to the customer account
  const { isLoading: isAddingProducts, mutate: addProducts } = useMutation(
    (products: ProductCreateEdit[]) =>
      apiFetch(
        `${localStorage.getItem(API_V1_HOSTNAME_KEY)}onboarding/proxy/products`,
        products,
        'POST'
      ),
    {
      onSuccess(response) {
        setSuccessMessage(
          t(`screens.products.sidekick.successCreate${response?.length > 1 ? 'Multiple' : ''}`)
        );
        if (response?.length > 0) {
          setHasAddedProducts(true);
        }
      },
      onError(err: ErrorObject) {
        if ((err as any)?.code === 401) return;
        Sentry.captureException(new Error('Error creating products'), {
          extra: { error: JSON.stringify(err) },
        });
      },
    }
  );

  // Refetch the businessSuggestions Id, that'll then trigger the refetch of the product suggestions
  const retryFetchProducts = () => {
    setIsStartingSidekick(true);
    const currentIndustryType = industryTypes?.find(
      (industryType) => industryType.IndustryTypeId === businessInformation?.industryType
    );

    if (businessInformation?.town) {
      // Fetches the id to get the suggestions. Once the id is available, it'll enable the fetch of the suggestions
      getBusinessSuggestionsId({
        industryType: currentIndustryType?.Name,
        location: businessInformation?.town,
      });
    } else {
      const countryName = countries?.find(
        (country) => country?.value === businessInformation?.country
      )?.label;

      // Fetches the id to get the suggestions. Once the id is available, it'll enable the fetch of the suggestions
      getBusinessSuggestionsId({
        industryType: currentIndustryType?.Name,
        location: countryName,
      });
    }
  };

  useEffect(() => {
    // Triggered when retryFetchProducts
    if (businessSuggestionsId !== '') {
      refetch();
    }
  }, [businessSuggestionsId]);

  useEffect(() => {
    const currentIndustryType = industryTypes?.find(
      (industryType) => industryType.IndustryTypeId === businessInformation?.industryType
    );

    if (businessInformation?.town) {
      // Fetches the id to get the suggestions. Once the id is available, it'll enable the fetch of the suggestions
      getBusinessSuggestionsId({
        industryType: currentIndustryType?.Name,
        location: businessInformation?.town,
      });
    } else {
      const countryName = countries?.find(
        (country) => country?.value === businessInformation?.country
      )?.label;

      // Fetches the id to get the suggestions. Once the id is available, it'll enable the fetch of the suggestions
      getBusinessSuggestionsId({
        industryType: currentIndustryType?.Name,
        location: countryName,
      });
    }
  }, [businessInformation]);

  const handleNextStepClick = () => {
    if (hasAddedProducts) {
      // Products added go to the next step
      goToNextStep();
    } else {
      // Show the confirmation modal
      setIsNoProductPopupOpen(true);
    }
  };

  const handleClosePopup = () => {
    setIsNoProductPopupOpen(false);
  };

  const handleSkipInPopup = () => {
    handleClosePopup();
    goToNextStep();
  };

  return (
    <>
      <PageHeader omitTextHeader />

      {isError && <ErrorLoadingData action={refetchBusinessInformation} />}

      {!isError &&
        (isLoading ||
        isRefetching ||
        isLoadingCategories ||
        isLoadingBusinessSuggestionId ||
        isLoadingBusinessInfo ||
        isStartingSidekick ? (
          <Card
            variant="outlined"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '1.5rem',
              marginBottom: '3rem',
            }}
          >
            {(isStartingSidekick || isRefetching) && (
              <Typography variant="body1" sx={{ marginTop: '1rem' }}>
                {t('screens.products.sidekick.generatingProducts')}
              </Typography>
            )}
            <Loader
              ariaBusy={
                isLoading ||
                isRefetching ||
                isLoadingCategories ||
                isLoadingBusinessSuggestionId ||
                isLoadingBusinessInfo ||
                isStartingSidekick
              }
            />
          </Card>
        ) : (
          <ProductTableCompleteWrapper>
            {isMobile ? (
              <SidekickMobileProductsTable
                addProducts={addProducts}
                isAddingProducts={isAddingProducts}
                refetchProducts={retryFetchProducts}
                categories={categoriesData}
                tableData={tableData}
                isLoading={
                  isLoading ||
                  isRefetching ||
                  isLoadingCategories ||
                  isLoadingBusinessSuggestionId ||
                  isLoadingBusinessInfo ||
                  isStartingSidekick
                }
                emptyStateTitle={t('actions.error')}
                emptyStateSubtitle={t('screens.products.sidekick.noProductsGenerated')}
              />
            ) : (
              <SidekickDesktopProductTable
                addProducts={addProducts}
                isAddingProducts={isAddingProducts}
                data={tableData}
                loading={
                  isLoading ||
                  isRefetching ||
                  isLoadingCategories ||
                  isLoadingBusinessSuggestionId ||
                  isLoadingBusinessInfo ||
                  isStartingSidekick
                }
                categories={categoriesData}
                refetchProducts={retryFetchProducts}
              />
            )}
          </ProductTableCompleteWrapper>
        ))}

      <SnackBarMessage
        message={successMessage}
        setMessage={setSuccessMessage}
        isMobile={isMobile}
        theme={theme}
        locale={locale}
      />

      <SkipStepPopup
        open={isNoProductPopupOpen}
        handleClose={handleClosePopup}
        handleSkip={handleSkipInPopup}
        popupTitle={t('screens.products.sidekick.noProductsAddedTitle')}
        popupDescription={t('screens.products.sidekick.noProductsAddedDescription')}
        // Override button text
        cancelButtonText={t('actions.goBack')}
        skipButtonText={t('actions.continueAnyway')}
      />

      <ActionsContainer>
        {(!isMobile || tableData?.length > 0) && (
          <Button
            variant="outlined"
            sx={{
              margin: '0px',
              flexGrow: '0',
              height: '48px',
            }}
            onClick={() => {
              navigate('/products');
            }}
          >
            {t('actions.previous')}
          </Button>
        )}

        {tableData?.length > 0 ? (
          <Button
            variant="contained"
            sx={{
              margin: '0px',
              flexGrow: '0',
              height: '48px',
            }}
            onClick={handleNextStepClick}
          >
            {t('actions.next')}
          </Button>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              ...(isMobile &&
                tableData?.length < 1 && {
                  justifyContent: 'space-between',
                  width: '100%',
                }),
              gap: '0.5rem',
            }}
          >
            <Button
              variant="outlined"
              sx={{
                margin: '0px',
                flexGrow: '0',
                height: '48px',
              }}
              onClick={goToNextStep}
            >
              {t('actions.skip')}
            </Button>
            <Button
              variant="contained"
              sx={{
                margin: '0px',
                flexGrow: '0',
                height: '48px',
              }}
              onClick={() => navigate('/products-categories')}
            >
              {t(`screens.products.sidekick.${isMobile ? 'mobileManualAdd' : 'manualAdd'}`)}
            </Button>
          </Box>
        )}
      </ActionsContainer>
    </>
  );
};

export default SidekickProducts;
